import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { isMockAuth } from "../auth/config";
import AddObjectiveForm from "../components/AddObjectiveForm";
import BookmarksSection from "../components/BookmarksSection";
import ObjectiveItem from "../components/ObjectiveItem";
import SkillBadges from "../components/SkillBadges";
import {
  addBookmark as apiAddBookmark,
  ApiBookmark,
  deleteBookmark as apiDeleteBookmark,
  getVideoDetail,
  isApiConfigured,
  patchBookmark as apiPatchBookmark,
  transcribeVideo as apiTranscribeVideo,
} from "../lib/api-client";
import { useYouTubePlayer } from "../lib/use-youtube-player";
import {
  createApiLibraryStore,
  createLocalLibraryStore,
  UserVideo,
  VideoStatus,
} from "../lib/library-store";
import {
  createApiObjectivesStore,
  createLocalObjectivesStore,
  Objective,
  sampleObjectivesFor,
  VideoObjectives,
} from "../lib/objectives-store";
import {
  createApiProgressStore,
  createLocalProgressStore,
  ObjectiveProgress,
} from "../lib/progress-store";

const API_STORES = isApiConfigured && !isMockAuth;

function nextStatus(current: VideoStatus, anyChecked: boolean, allChecked: boolean): VideoStatus {
  if (allChecked) return "complete";
  if (anyChecked) return "in-progress";
  if (current === "complete" || current === "in-progress") return "ready";
  return current;
}

export default function VideoDetail() {
  const { videoId = "" } = useParams<{ videoId: string }>();
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();

  const libraryStore = useMemo(() => {
    if (!user) return null;
    return API_STORES ? createApiLibraryStore(getIdToken) : createLocalLibraryStore(user.sub);
  }, [user, getIdToken]);
  const objectivesStore = useMemo(
    () => (API_STORES ? createApiObjectivesStore(getIdToken) : createLocalObjectivesStore()),
    [getIdToken]
  );
  const progressStore = useMemo(() => {
    if (!user) return null;
    return API_STORES ? createApiProgressStore(getIdToken) : createLocalProgressStore(user.sub);
  }, [user, getIdToken]);

  const [video, setVideo] = useState<UserVideo | null>(null);
  const [record, setRecord] = useState<VideoObjectives | null>(null);
  const [progress, setProgress] = useState<Record<string, ObjectiveProgress>>({});
  const [bookmarks, setBookmarks] = useState<ApiBookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [transcribeBusy, setTranscribeBusy] = useState(false);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);

  const {
    setContainer: setPlayerContainer,
    getCurrentTime: getPlayerTime,
    seekTo: seekPlayer,
  } = useYouTubePlayer(videoId);

  const refresh = useCallback(async () => {
    if (!libraryStore || !progressStore) return;
    const [v, rec, prog] = await Promise.all([
      libraryStore.get(videoId),
      objectivesStore.get(videoId),
      progressStore.forVideo(videoId),
    ]);
    setVideo(v);
    setRecord(rec);
    setProgress(prog);
    if (API_STORES) {
      try {
        const token = await getIdToken();
        if (token) {
          const detail = await getVideoDetail(videoId, token);
          setBookmarks(detail?.bookmarks ?? []);
        }
      } catch {
        // Non-fatal: video already loaded from the store; bookmarks stay empty.
      }
    } else {
      setBookmarks([]);
    }
    setLoading(false);
  }, [getIdToken, libraryStore, objectivesStore, progressStore, videoId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Auto-poll while a transcription is in flight. The captions Lambda is
  // invoked async and `transcribeVideo` returns 202 immediately, so the first
  // `refresh()` always sees status="transcribing". Without this, the UI sits
  // there until the user manually clicks Refresh/Retry — the actual extraction
  // typically finishes in 30-90s, so we re-fetch every 4s until status flips
  // to "ready" (or we give up after 2 min and fall back to manual Retry).
  useEffect(() => {
    if (!API_STORES) return;
    if (video?.status !== "transcribing") return;
    const startedAt = Date.now();
    const POLL_INTERVAL_MS = 4000;
    const MAX_POLL_MS = 2 * 60 * 1000;
    const timer = setInterval(() => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        clearInterval(timer);
        return;
      }
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [video?.status, refresh]);

  const updateStatusFromProgress = useCallback(
    async (nextProgress: Record<string, ObjectiveProgress>, rec: VideoObjectives | null) => {
      if (!video || !libraryStore || !rec) return;
      const total = rec.objectives.length;
      const checkedCount = rec.objectives.filter((o) => nextProgress[o.id]?.checked).length;
      const status = nextStatus(video.status, checkedCount > 0, total > 0 && checkedCount === total);
      if (status !== video.status) {
        const updated = await libraryStore.update(videoId, { status });
        if (updated) setVideo(updated);
      }
    },
    [libraryStore, video, videoId]
  );

  async function onToggle(objectiveId: string, checked: boolean) {
    if (!progressStore) return;
    await progressStore.setChecked(videoId, objectiveId, checked);
    const nextProgress = await progressStore.forVideo(videoId);
    setProgress(nextProgress);
    await updateStatusFromProgress(nextProgress, record);
  }

  async function onEditObjective(objectiveId: string, patch: Partial<Objective>) {
    const updated = await objectivesStore.updateObjective(videoId, objectiveId, patch);
    if (updated) setRecord({ ...updated });
  }

  async function onRemoveObjective(objectiveId: string) {
    const updated = await objectivesStore.removeObjective(videoId, objectiveId);
    if (updated) {
      setRecord({ ...updated });
      await updateStatusFromProgress(progress, updated);
    }
  }

  async function onAddObjective(objective: Objective) {
    let updated = await objectivesStore.addObjective(videoId, objective);
    if (!updated && record) {
      updated = { ...record, objectives: [...record.objectives, objective] };
      await objectivesStore.put(updated);
    }
    if (updated) {
      setRecord({ ...updated });
      await updateStatusFromProgress(progress, updated);
    }
  }

  async function generateStubs() {
    const stub = sampleObjectivesFor(videoId);
    await objectivesStore.put(stub);
    setRecord(stub);
    if (libraryStore && video && video.status === "bookmarked") {
      const updated = await libraryStore.update(videoId, { status: "ready" });
      if (updated) setVideo(updated);
    }
  }

  async function triggerTranscription() {
    if (!API_STORES) return;
    setTranscribeError(null);
    setTranscribeBusy(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error("Sign in again to continue.");
      await apiTranscribeVideo(videoId, token);
      await refresh();
    } catch (e) {
      setTranscribeError(e instanceof Error ? e.message : String(e));
    } finally {
      setTranscribeBusy(false);
    }
  }

  const onSeek = useCallback(
    (seconds: number) => {
      seekPlayer(seconds);
    },
    [seekPlayer],
  );

  async function onAddBookmark(label: string) {
    if (!API_STORES) return;
    const token = await getIdToken();
    if (!token) throw new Error("Sign in again to bookmark.");
    const timestampSec = Math.max(0, Math.floor(getPlayerTime()));
    const created = await apiAddBookmark(videoId, { timestampSec, label }, token);
    setBookmarks((prev) =>
      [...prev, created].sort((a, b) => a.timestampSec - b.timestampSec),
    );
  }

  async function onEditBookmark(id: string, patch: { label?: string; timestampSec?: number }) {
    if (!API_STORES) return;
    const token = await getIdToken();
    if (!token) throw new Error("Sign in again to edit bookmarks.");
    const updated = await apiPatchBookmark(videoId, id, patch, token);
    setBookmarks((prev) =>
      prev
        .map((b) => (b.id === id ? updated : b))
        .sort((a, b) => a.timestampSec - b.timestampSec),
    );
  }

  async function onDeleteBookmark(id: string) {
    if (!API_STORES) return;
    const token = await getIdToken();
    if (!token) throw new Error("Sign in again to delete bookmarks.");
    await apiDeleteBookmark(videoId, id, token);
    setBookmarks((prev) => prev.filter((b) => b.id !== id));
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Video not in your library.</p>
          <button
            type="button"
            onClick={() => navigate("/library", { replace: true })}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded"
          >
            Back to library
          </button>
        </div>
      </div>
    );
  }

  const total = record?.objectives.length ?? 0;
  const checkedCount = record?.objectives.filter((o) => progress[o.id]?.checked).length ?? 0;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-800">
        <div className="flex items-center gap-4 min-w-0">
          <Link
            to="/library"
            className="text-sm text-gray-500 hover:text-orange-300 transition shrink-0 inline-flex items-center gap-1.5"
          >
            <span aria-hidden>←</span>
            <span className="font-display tracking-wide">Library</span>
          </Link>
          <span aria-hidden className="text-gray-800 shrink-0">
            |
          </span>
          <h1
            className="text-sm sm:text-base text-gray-200 truncate font-medium"
            title={video.title}
          >
            {video.title}
          </h1>
        </div>
        <a
          href={`https://www.youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-gray-500 hover:text-gray-300 transition shrink-0"
        >
          Open on YouTube ↗
        </a>
      </header>

      <main className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 max-w-7xl mx-auto">
        <section className="lg:col-span-2 space-y-4">
          <div
            ref={setPlayerContainer}
            className="aspect-video bg-black rounded-lg overflow-hidden"
          />
          <div className="space-y-3">
            <p className="text-sm text-gray-400 leading-relaxed">
              <span className="text-gray-200 font-medium">{video.channel}</span>
              {record?.summary && (
                <>
                  <span className="text-gray-700 mx-2">·</span>
                  <span>{record.summary}</span>
                </>
              )}
            </p>

            <div className="flex items-center flex-wrap gap-3">
              {(video.skillCategory ||
                video.difficulty ||
                (video.techniques?.length ?? 0) > 0) && (
                <SkillBadges
                  skillCategory={video.skillCategory ?? null}
                  difficulty={video.difficulty ?? null}
                  techniques={video.techniques ?? []}
                  variant="detail"
                />
              )}

              {record?.transcriptSource && (
                <span
                  title={
                    record.transcriptSource === "whisper"
                      ? "No YouTube caption track was available — transcript came from OpenAI Whisper on the extracted audio."
                      : "Transcript came from this video's YouTube caption track."
                  }
                  className="inline-flex items-center gap-1.5 text-[11px] font-display tracking-[0.18em] uppercase text-gray-500"
                >
                  <span
                    aria-hidden
                    className={`h-1.5 w-1.5 rounded-full ${
                      record.transcriptSource === "whisper"
                        ? "bg-amber-400"
                        : "bg-emerald-400"
                    }`}
                  />
                  {record.transcriptSource === "whisper"
                    ? "Transcribed from audio"
                    : "Transcribed from captions"}
                </span>
              )}
            </div>
          </div>
          {API_STORES && (
            <BookmarksSection
              bookmarks={bookmarks}
              onSeek={onSeek}
              onAdd={onAddBookmark}
              onEdit={onEditBookmark}
              onDelete={onDeleteBookmark}
            />
          )}
        </section>

        <aside className="lg:col-span-1">
          <div className="mb-3 space-y-2">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-100">
                Practice checklist
              </h2>
              {total > 0 && (
                <span
                  data-testid="objectives-progress"
                  className="font-display-italic text-sm text-gray-500 tabular-nums"
                >
                  {checkedCount} of {total}
                </span>
              )}
            </div>
            {total > 0 && (
              <div
                role="progressbar"
                aria-valuenow={checkedCount}
                aria-valuemax={total}
                className="h-px bg-gray-800/80"
              >
                <div
                  className="h-full bg-orange-400/80 transition-all duration-500"
                  style={{ width: `${(checkedCount / total) * 100}%` }}
                />
              </div>
            )}
          </div>

          {!record ? (
            <div className="p-4 rounded border border-dashed border-gray-700 text-center space-y-3">
              {API_STORES ? (
                <>
                  {video.status === "transcribing" ? (
                    <>
                      <p className="text-sm text-gray-200">Transcribing…</p>
                      <p className="font-display-italic text-xs text-gray-500">
                        objectives appear when extraction finishes — usually
                        under a minute.
                      </p>
                      <p className="text-[11px] text-gray-600">
                        Stuck? Retry below — it&apos;s idempotent.
                      </p>
                    </>
                  ) : video.status === "quota-exceeded" ? (
                    <>
                      <p className="text-sm text-gray-200">
                        Daily transcription budget reached.
                      </p>
                      <p className="font-display-italic text-xs text-gray-500 leading-relaxed">
                        This video has no captions, so it needs audio
                        transcription — and today&apos;s budget is used up. It
                        resets overnight; retry tomorrow.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 leading-relaxed">
                      No objectives yet.{" "}
                      <span className="text-gray-500">
                        Pull them from the captions to build a practice
                        checklist.
                      </span>
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => void triggerTranscription()}
                    disabled={transcribeBusy}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm rounded"
                  >
                    {transcribeBusy
                      ? "Working…"
                      : video.status === "transcribing"
                        ? "Retry transcription"
                        : video.status === "quota-exceeded"
                          ? "Try again"
                          : "Extract objectives now"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void refresh()}
                    className="ml-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded"
                  >
                    Refresh
                  </button>
                  {transcribeError && <p className="text-xs text-red-400">{transcribeError}</p>}
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500">
                    No objectives yet. The M1.4 pipeline will extract these from the video
                    transcript.
                  </p>
                  <button
                    type="button"
                    onClick={() => void generateStubs()}
                    className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded"
                  >
                    Generate sample objectives
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <ul data-testid="objectives-list" className="space-y-2">
                {record.objectives.map((o) => (
                  <ObjectiveItem
                    key={o.id}
                    objective={o}
                    progress={progress[o.id]}
                    onToggle={(c) => void onToggle(o.id, c)}
                    onSeek={onSeek}
                    onEdit={(patch) => void onEditObjective(o.id, patch)}
                    onRemove={() => void onRemoveObjective(o.id)}
                  />
                ))}
              </ul>
              <AddObjectiveForm onAdd={(obj) => void onAddObjective(obj)} />
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}

